import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * 一次只显示一条的轻提示。
 *
 * 刻意做得这么小：主要反馈（校验错误、保存失败）走页面内的 banner，因为
 * 它们需要留在屏幕上被读到；toast 只用于「发生过、已经过去」的确认，如
 * 「已复制」「已删除」。一个什么都往里塞的 toast 会让真正重要的错误也被
 * 三秒后消失。
 */
interface ToastValue {
  show: (message: string) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const show = useCallback((next: string) => {
    setMessage(next);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMessage(null), 2600);
  }, []);

  // 组件卸载时清掉待触发的 timeout，避免对已卸载组件写 state。
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {message !== null && (
        <div className="toast-wrap" role="status">
          <div className="toast">{message}</div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) throw new Error("useToast 必须在 ToastProvider 内使用");
  return ctx;
}
